import { useEffect, useState } from "react";
import { Send, MousePointerClick, Ticket, Coins, Download, AlertTriangle, Pencil } from "lucide-react";
import { EditCampaignDialog } from "./EditCampaignDialog";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { StatCard, btnGhost, fmtDate, fmtKr, downloadBlob } from "./shared";

export default function Resultater() {
  const [data, setData] = useState({ items: [], totals: {}, sms_konfigurert: true });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("aktiv");
  const [editing, setEditing] = useState(null);

  useEffect(() => {
    api
      .get("/admin/campaigns")
      .then((res) => setData(res.data))
      .catch((err) => toast.error(formatApiErrorDetail(err.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, []);

  const exportCampaign = async (c) => {
    try {
      const res = await api.get(`/admin/campaigns/${c.id}/export`, { responseType: "blob" });
      downloadBlob(res.data, `kampanje_${c.navn.replace(/\s+/g, "_")}.csv`);
    } catch {
      toast.error("Kunne ikke eksportere");
    }
  };

  const t = data.totals;
  const counts = { aktiv: data.items.filter((c) => c.status === "aktiv").length, utlopt: data.items.filter((c) => c.status === "utlopt").length };
  const visible = filter === "alle" ? data.items : data.items.filter((c) => c.status === filter);
  const onSaved = (updated) => {
    setData((d) => ({ ...d, items: d.items.map((c) => (c.id === updated.id ? updated : c)) }));
    setEditing(null);
  };
  const pct = (a, b) => (b ? `${Math.round((a / b) * 100)} %` : "—");

  return (
    <div data-testid="resultater-page">
      <h1 className="font-serif-display text-3xl font-600 text-[#FDFBF7]">Resultat av kampanjer</h1>

      {!data.sms_konfigurert && (
        <div className="mt-4 flex items-start gap-3 rounded-xl border border-amber-400/30 bg-amber-400/10 px-4 py-3 text-sm text-amber-200" data-testid="sms-warning">
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
          SMS-utsending er ikke konfigurert ennå. Kampanjer opprettes med personlige kupong-lenker som kan lastes ned som CSV.
        </div>
      )}

      <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard icon={Send} label="Leverte kampanjer" value={t.levert ?? 0} sub={`${t.kampanjer ?? 0} kampanjer · ${t.mottakere ?? 0} mottakere`} testid="stat-levert" />
        <StatCard icon={MousePointerClick} label="Klikk" value={t.klikk ?? 0} sub={`${pct(t.klikk, t.mottakere)} av mottakerne`} testid="stat-klikk" />
        <StatCard icon={Ticket} label="Innløste kuponger" value={t.innlost ?? 0} sub={`${pct(t.innlost, t.mottakere)} av mottakerne`} testid="stat-innlost" />
        <StatCard icon={Coins} label="Attribuert omsetning" value={fmtKr(t.omsetning)} testid="stat-omsetning" />
      </div>

      {editing && <EditCampaignDialog campaign={editing} onClose={() => setEditing(null)} onSaved={onSaved} />}

      <div className="mt-6 flex flex-wrap gap-2" data-testid="campaign-filter">
        {[
          { id: "aktiv", label: "Aktive", n: counts.aktiv },
          { id: "utlopt", label: "Inaktive / utløpte", n: counts.utlopt },
          { id: "alle", label: "Alle", n: data.items.length },
        ].map((f) => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            data-testid={`filter-${f.id}`}
            className={`rounded-full border px-4 py-2 text-sm transition-colors ${
              filter === f.id ? "border-[#D4AF37] bg-[#D4AF37] text-[#1A1715]" : "border-white/10 text-[#FDFBF7] hover:border-[#D4AF37]/50"
            }`}
          >
            {f.label} <span className="opacity-70">({f.n})</span>
          </button>
        ))}
      </div>

      <div className="mt-4 overflow-x-auto rounded-2xl border border-white/10 bg-[#282421]">
        <table className="w-full min-w-[900px] text-left text-sm" data-testid="campaigns-table">
          <thead>
            <tr className="border-b border-white/10 text-xs uppercase tracking-wide text-[#A8A29E]">
              <th className="px-4 py-4">Kampanje</th>
              <th className="px-4 py-4">Gruppe</th>
              <th className="px-4 py-4">Sendt</th>
              <th className="px-4 py-4">Utløper</th>
              <th className="px-4 py-4 text-center">Mottakere</th>
              <th className="px-4 py-4 text-center">Levert</th>
              <th className="px-4 py-4 text-center">Klikk</th>
              <th className="px-4 py-4 text-center">Innløst</th>
              <th className="px-4 py-4 text-right">Omsetning</th>
              <th className="px-4 py-4 text-right"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={10} className="px-4 py-10 text-center text-[#A8A29E]">Laster…</td></tr>
            ) : visible.length === 0 ? (
              <tr><td colSpan={10} className="px-4 py-10 text-center text-[#A8A29E]" data-testid="empty-state">Ingen kampanjer her.</td></tr>
            ) : (
              visible.map((c) => (
                <tr key={c.id} className="border-b border-white/5 transition-colors hover:bg-white/5" data-testid={`campaign-row-${c.id}`}>
                  <td className="px-4 py-4">
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-[#FDFBF7]">{c.navn}</p>
                      <span
                        className={`rounded-full px-2 py-0.5 text-[0.65rem] font-medium ${c.status === "aktiv" ? "bg-[#22c55e]/10 text-[#22c55e]" : "bg-white/5 text-[#A8A29E]"}`}
                        data-testid={`status-${c.id}`}
                      >
                        {c.status === "aktiv" ? "Aktiv" : "Utløpt"}
                      </span>
                    </div>
                    <p className="text-xs text-[#A8A29E]">{c.tittel}</p>
                    {c.tittel_en && c.tittel_en !== c.tittel && <p className="text-xs text-[#A8A29E]/70" data-testid={`campaign-title-en-${c.id}`}>EN: {c.tittel_en}</p>}
                  </td>
                  <td className="px-4 py-4 text-[#A8A29E]">{c.gruppe_navn}{c.aldersgruppe ? ` · ${c.aldersgruppe}` : ""}</td>
                  <td className="px-4 py-4 text-xs text-[#A8A29E]">{fmtDate(c.opprettet)}</td>
                  <td className="px-4 py-4 text-xs text-[#A8A29E]" data-testid={`expiry-${c.id}`}>{fmtDate(c.utloper)}</td>
                  <td className="px-4 py-4 text-center text-[#FDFBF7]">{c.antall_mottakere}</td>
                  <td className="px-4 py-4 text-center text-[#FDFBF7]" data-testid={`levert-${c.id}`}>{c.levert}</td>
                  <td className="px-4 py-4 text-center text-[#FDFBF7]" data-testid={`klikk-${c.id}`}>{c.klikk}</td>
                  <td className="px-4 py-4 text-center font-semibold text-[#D4AF37]" data-testid={`innlost-${c.id}`}>{c.innlost}</td>
                  <td className="px-4 py-4 text-right text-[#FDFBF7]" data-testid={`omsetning-${c.id}`}>{fmtKr(c.omsetning)}</td>
                  <td className="px-4 py-4 text-right">
                    <div className="flex justify-end gap-1.5">
                      {c.status === "aktiv" && (
                        <button onClick={() => setEditing(c)} data-testid={`edit-campaign-${c.id}`} className={`${btnGhost} !px-3 !py-1.5 !text-xs`}>
                          <Pencil className="h-3.5 w-3.5" /> Rediger
                        </button>
                      )}
                      <button onClick={() => exportCampaign(c)} data-testid={`export-campaign-${c.id}`} className={`${btnGhost} !px-3 !py-1.5 !text-xs`}>
                        <Download className="h-3.5 w-3.5" /> CSV
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
